import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/utils";
import { useLocation, useNavigate } from "react-router-dom";

const LANGS = ["en", "pt", "es"];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const links = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.insurance, href: "#insurance" },
    { label: t.nav.fleet, href: "#fleet" },
    { label: t.nav.areas, href: "#areas" },
  ];

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
    } else {
      scrollToId(href);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={"fixed top-0 inset-x-0 z-50 bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-black/10 shadow-sm"}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <button onClick={() => go("#top")} className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
          <img src="/assets/logo-icon.png" alt="ClearRoute Auto Glass" className="h-10 w-auto" />
          <span className="font-display font-bold text-lg tracking-tight leading-none">
            <span className="text-[#D4AF37]">Clear</span>
            <span className="text-[#111111]">Route</span>
            <span className="block text-[10px] font-medium tracking-[0.3em] text-black/45 uppercase">Auto Glass</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-medium text-black/60 hover:text-[#B8860B] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center rounded-full border border-black/15 p-0.5" data-testid="language-switcher">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors duration-300 ${
                  lang === l ? "bg-[#D4AF37] text-black" : "text-black/50 hover:text-black"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => go("#quote")}
            data-testid="nav-quote-cta"
            className="hidden md:inline-flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#E6C965] text-black text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-300 hover:scale-105 transform"
          >
            {t.nav.quote}
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            className="lg:hidden text-black/70 p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden glass-panel overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left text-lg font-display font-semibold text-black/80 hover:text-[#B8860B] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex sm:hidden items-center gap-2 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase border ${
                      lang === l ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-black/50 border-black/20"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button
                onClick={() => go("#quote")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold px-5 py-3 rounded-full"
              >
                {t.nav.quote}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
